import {
  useState,
  useCallback,
  ChangeEvent,
  useEffect,
  useMemo,
} from "react";
import { settingsService } from "../services/settings.service";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { ILicense } from "Dashboard/domain/ILicense";
import { Pagination } from "swiper";
import { debounce } from "lodash";
import { IEmailData } from "Dashboard/domain/IEmailData";
import { IMemorandumData } from "Dashboard/domain/IMemorandumData";
import UploadPhotoModal from "./UploadPhotoModal";
import { ImageUploadType } from "Dashboard/domain/ImageUploadType";
import { toast } from "react-toastify";
import { IChangeHourlyRateNameRequestParams } from "Dashboard/domain/hourly_work/IChangeHourlyRateNameRequestParams";
import { IAddHourlyRateNameRequestParams } from "Dashboard/domain/hourly_work/IAddHourlyRateNameRequestParams";
import { useSelector } from "react-redux";
import { addHourlyRateName, changeHourlyRateName, selectState as selectStateDashboard, updateHourlyRateNameTemporarily, updateProvision, updateProvisionTemporarily } from "Dashboard/dashboard.slice";
import { useAppDispatch } from "hooks/hooks";
import NumberInput from "./NumberInput";
import { IUpdateProvisionRequestParams } from "Dashboard/domain/IUpdateProvisionRequestParams";

interface IProps {
  setClickOutsideActive: Function;
}

const SettingsSlider = ({ setClickOutsideActive }: IProps) => {
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [memorandumLogo, setMemorandumLogo] = useState<string | null>(null);

  const [license, setLicense] = useState<ILicense>({
    companyName: "",
    address: "",
    contactPersonName: "",
    identificationNumber: "",
    landlinePhone: "",
    mobilePhone: "",
    taxId: "",
  });

  const { provisions } = useSelector(selectStateDashboard);

  const [newHourlyRateLabel, setNewHourlyRateLabel] = useState<string>("");

  const [memorandumData, setMemorandumData] = useState<IMemorandumData>({
    showOnAccount: false,
    instruction: "",
  });

  const [emailData, setEmailData] = useState<IEmailData>({
    sendForChat: false,
    sendForClaimsAndPrivateMessages: false,
    senderEmail: "",
    smtpServerHost: "",
    smtpServerPort: 0,
    username: "",
    password: "",
  });

  const delayedQueryLicense = useMemo(
    () => debounce((q) => settingsService.updateLicense(q), 1000),
    []
  );

  const delayedUpdateProvision = useMemo(
    () => debounce(async (params) => {
      try {
        dispatch(updateProvision(params));
        toast.success("Uspešno izmenjena količina karneta za odsustvo.");
      } catch (err: any) {
        toast.error("Nije uspela izmena količine karneta za odsustvo.");
      }
    }, 1000),
    [dispatch]
  );

  const delayedChangeHourlyRateName = useMemo(
    () => debounce(async (params: IChangeHourlyRateNameRequestParams) => {
      try {
        await dispatch(changeHourlyRateName(params));
        toast.success("Uspešno izmenjena satnica u karnetima.");
      } catch (err: any) {
        toast.error("Nije uspela izmena satnice u karnetima.");
      }
    }, 1000),
    [dispatch]
  );

  const delayedQueryMemorandumData = useMemo(
    () => debounce((q) => settingsService.updateMemorandumData(q), 1000),
    []
  );

  const delayedQueryEmailData = useMemo(
    () => debounce((q) => settingsService.updateEmailData(q), 1000),
    []
  );

  const refreshLogo = useCallback(async () => {
    try {
      let logo = await settingsService.getMemorandumLogo();
      setMemorandumLogo(logo);
    } catch (err) {
      console.log(err);
    }
  }, []);

  useEffect(() => {
    async function getData() {
      try {
        let [license, memorandumData, emailData, logo] =
          await Promise.all([
            settingsService.getLicense(),
            settingsService.getMemorandumData(),
            settingsService.getEmailData(),
            settingsService.getMemorandumLogo(),
          ]);
        setLicense(license);
        setMemorandumData(memorandumData);
        setEmailData(emailData);
        setMemorandumLogo(logo);
      } catch (e) { }
    }
    getData();
  }, []);

  const onChangeLicense = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let newValue = { ...license, [name]: value };
    setLicense(newValue);
    delayedQueryLicense(newValue);
  };

  const onChangeProvision = (params: IUpdateProvisionRequestParams) => {
    dispatch(updateProvisionTemporarily(params));
    delayedUpdateProvision(params);
  };

  const onChangeHourlyRateName = (params: IChangeHourlyRateNameRequestParams) => {
    dispatch(updateHourlyRateNameTemporarily(params));
    delayedChangeHourlyRateName(params);
  }

  const onAddHourlyRateName = async (params: IAddHourlyRateNameRequestParams) => {
    try {
      if (newHourlyRateLabel) {
        await dispatch(addHourlyRateName(params));
        setNewHourlyRateLabel("");
        toast.success("Uspešno dodata satnica u karnetima.");
      } else {
        toast.warn("Unesite naziv za novu satnicu u karnetima.");
      }
    } catch (err: any) {
      toast.error("Nije uspelo dodavanje satnice u karnetima.");
    }
  }

  const onChangeMemorandumData = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    let newValue = {
      ...memorandumData,
      [name]: type === "text" ? value : checked,
    };
    setMemorandumData(newValue);
    delayedQueryMemorandumData(newValue);
  };

  const onChangeMemorandumDataTA = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    let newValue = { ...memorandumData, [name]: value };
    setMemorandumData(newValue);
    delayedQueryMemorandumData(newValue);
  };

  const onChangeEmailData = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked, type } = e.target;
    let newValue = { ...emailData, [name]: type === "text" ? value : checked };
    setEmailData(newValue);
    delayedQueryEmailData(newValue);
  };

  const openPhotoUploadModal = () => {
    setIsOpen(true);
    setClickOutsideActive(false);
  };

  return (
    <>
      <Swiper
        allowTouchMove={false}
        modules={[Pagination]}
        slidesPerView={1}
        pagination={{
          clickable: true,
          bulletClass: "swiper-pagination-custom-bullet",
          bulletActiveClass: "swiper-pagination-custom-bullet-active",
        }}
      >
        <SwiperSlide className="swiper-inputs">
          <span className="settings-tooltip-header w55">Registrovano na</span>
          <input
            type="text"
            className="settings-input company-name"
            name="companyName"
            value={license.companyName ? license.companyName : ""}
            placeholder="Naziv kompanije"
            onChange={onChangeLicense}
          />
          <br />
          <input
            type="text"
            className="settings-input w80"
            name="address"
            value={license.address ? license.address : ""}
            placeholder="Adresa"
            onChange={onChangeLicense}
          />
          <input
            type="text"
            className="settings-input w80"
            name="identificationNumber"
            value={
              license.identificationNumber ? license.identificationNumber : ""
            }
            placeholder="Matični broj"
            onChange={onChangeLicense}
          />
          <input
            type="text"
            className="settings-input w80"
            name="taxId"
            value={license.taxId ? license.taxId : ""}
            placeholder="PIB"
            onChange={onChangeLicense}
          />
          <input
            type="text"
            className="settings-input w80"
            name="contactPersonName"
            value={license.contactPersonName ? license.contactPersonName : ""}
            placeholder="Ime kontakt osobe"
            onChange={onChangeLicense}
          />
          <input
            type="text"
            className="settings-input w80"
            name="landlinePhone"
            value={license.landlinePhone ? license.landlinePhone : ""}
            placeholder="Telefon"
            onChange={onChangeLicense}
          />
          <input
            type="text"
            className="settings-input w80"
            name="mobilePhone"
            value={license.mobilePhone ? license.mobilePhone : ""}
            placeholder="Mobilni telefon"
            onChange={onChangeLicense}
          />
          <br />
        </SwiperSlide>
        <SwiperSlide>
          <span className="settings-tooltip-header w60">
            Bolovanje, odmor
          </span>
          <br />
          <div className="settings-row">
            <NumberInput
              formatAs="quantity"
              value={`${provisions?.sickLeaveCarnets}`}
              onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                if (parsedValue !== provisions?.sickLeaveCarnets) {
                  onChangeProvision({ sickLeaveCarnets: parsedValue });
                }
              }}
              className="settings-input w20 center"
            />
            <span className="settings-label">karneta dan bolovanja</span>
          </div>
          <div className="settings-row">
            <NumberInput
              formatAs="quantity"
              value={`${provisions?.vacationCarnets}`}
              onChange={(value: string, unmaskedValue: string, parsedValue: number) => {
                if (parsedValue !== provisions?.vacationCarnets) {
                  onChangeProvision({ vacationCarnets: parsedValue });
                }
              }}
              className="settings-input w20 center"
            />
            <span className="settings-label">karneta dan odmora</span>
          </div>
          <br />
          <span className="settings-tooltip-header w60">
            Karnet sati
          </span>
          <br />
          {provisions?.allHourlyRateNames?.map((hourlyRateName, index) =>
          (
            <div className="settings-row">
              <input
                type="text"
                className="settings-input w20 center"
                value={hourlyRateName.name || ""}
                onChange={(event) => onChangeHourlyRateName({ index: index, label: event.target.value })}
              />
              <span className="settings-label-clickable" onClick={() => onChangeHourlyRateName({ index: index, active: !hourlyRateName.active })}>{hourlyRateName.active ? 'aktivan' : 'neaktivan'}</span>
            </div>
          ))}
          {(provisions?.allHourlyRateNames?.length ?? 0) < 5 && (
            <div className="settings-row">
              <input
                type="text"
                className="settings-input w20 center"
                value={newHourlyRateLabel}
                onChange={event => setNewHourlyRateLabel(event.target.value)}
              />
              <span className="settings-label-clickable" onClick={() => onAddHourlyRateName({ label: newHourlyRateLabel })}>dodaj satnicu</span>
            </div>
          )}
        </SwiperSlide>
        <SwiperSlide>
          <span className="settings-tooltip-header w60">
            Podešavanje izgleda računa
          </span>
          <div className="settings-tooltip-invoice">
            <div
              className="settings-tooltip-img"
              onClick={openPhotoUploadModal}
            >
              {memorandumLogo && (
                <img
                  src={memorandumLogo}
                  alt="test"
                  width="135px"
                  height="45px"
                />
              )}
            </div>
            <input
              type="checkbox"
              name="showOnAccount"
              checked={memorandumData.showOnAccount}
              onChange={onChangeMemorandumData}
            />
            <span>prikaži na računu</span>
          </div>
          <textarea
            style={{ resize: "none" }}
            className="settings-paying-info"
            name="instruction"
            placeholder="Instrukcije za placanje"
            value={memorandumData.instruction ? memorandumData.instruction : ""}
            onChange={onChangeMemorandumDataTA}
          />
          <br />
          <br />
          <br />
        </SwiperSlide>
        <SwiperSlide className="swiper-inputs">
          <span className="settings-tooltip-header w60">SMTP</span>
          <div className="settings-row">
            <input
              type="checkbox"
              name="sendForChat"
              checked={emailData.sendForChat}
              onChange={onChangeEmailData}
            />
            <span>uključi email sistem ta chat</span>
          </div>
          <div className="settings-row">
            <input
              type="checkbox"
              name="sendForClaimsAndPrivateMessages"
              checked={emailData.sendForClaimsAndPrivateMessages}
              onChange={onChangeEmailData}
            />
            <span>uključi email sistem za trebovanja i privatne poruke</span>
          </div>
          <br />
          <input
            type="text"
            className="settings-input w80"
            name="senderEmail"
            placeholder="Email posiljaoca"
            value={emailData.senderEmail ? emailData.senderEmail : ""}
            onChange={onChangeEmailData}
          />
          <input
            type="text"
            className="settings-input w80"
            name="smtpServerHost"
            placeholder="SMPT server host"
            value={emailData.smtpServerHost ? emailData.smtpServerHost : ""}
            onChange={onChangeEmailData}
          />
          <input
            type="text"
            className="settings-input w80"
            name="smtpServerPort"
            placeholder="SMTP server port"
            value={emailData.smtpServerPort || ""}
            onChange={onChangeEmailData}
          />
          <input
            type="text"
            className="settings-input w80"
            name="username"
            placeholder="Korisnicko ime"
            value={emailData.username ? emailData.username : ""}
            onChange={onChangeEmailData}
          />
          <input
            type="text"
            className="settings-input w80"
            name="password"
            placeholder="Lozinka"
            value={emailData.password ? emailData.password : ""}
            onChange={onChangeEmailData}
          />
        </SwiperSlide>
      </Swiper>
      <UploadPhotoModal
        uploadType={ImageUploadType.MEMORANDUM}
        isOpen={isOpen}
        user={null}
        refresh={refreshLogo}
        setIsOpen={setIsOpen}
        aspect={1400 / 300}
        setClickOutsideActive={setClickOutsideActive}
      />
    </>
  );
};

export default SettingsSlider;
